import { useMemo } from 'react';

import { Table, TableLoadingRow, useIsDeviceSize } from '@foradevelopment/fora-ui';
import { EmploymentViewModelNew } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { useAppSelector } from '../../../redux/hooks';
import { TableSortId } from '../../../common/types';
import { EmploymentTableSortId, EmploymentTableSortState } from './EmploymentTableSortState';
import { useShowIconColumn } from '../../../common/utils/useShowIconColumn';
import { useTypedTranslation } from '../../../i18n/i18n';
import { EmploymentsTableRow } from './EmploymentsTableRow';

type EmploymentsTableProps = {
  employmentList: EmploymentViewModelNew[];
  employmentListIndices: number[];
  sortTable: (sortId: EmploymentTableSortId) => void;
  sortState: EmploymentTableSortState;
};

export const EmploymentsTable = ({
  employmentList,
  employmentListIndices,
  sortTable,
  sortState,
}: EmploymentsTableProps) => {
  const loadingCurrentEmployments = useAppSelector(state => state.employment.loadingCurrentEmployments);
  const countries = useAppSelector(state => state.foraGlobal.countries);
  const { isMobile } = useIsDeviceSize();
  const { t } = useTypedTranslation();

  const showIconColumn = useShowIconColumn(employmentList);
  const tableColumns = useMemo(() => {
    let tableHeadCells = showIconColumn ? [{ name: '', id: TableSortId.InactiveOrFokParameterChoices }] : [];
    if (isMobile) {
      tableHeadCells = tableHeadCells.concat([
        { name: t('table.employee.columnId'), id: TableSortId.EmployeeId },
        { name: t('table.employee.columnName'), id: TableSortId.FullName },
      ]);
    } else {
      tableHeadCells = tableHeadCells.concat([
        { name: t('table.employee.columnId'), id: TableSortId.EmployeeId },
        { name: t('table.employee.columnFirstname'), id: TableSortId.FirstName },
        { name: t('table.employee.columnLastname'), id: TableSortId.LastName },
        { name: t('table.employee.columMarkup'), id: TableSortId.Markup },
      ]);
    }
    return tableHeadCells as { name: string; id: EmploymentTableSortId }[];
  }, [isMobile, showIconColumn, t]);

  return (
    <Table withBorderedRows withExpandableRows>
      <Table.Head>
        <Table.HeadRow>
          {tableColumns.map((cell, index) => {
            return (
              <Table.HeadCell
                key={`${cell.id}-${index}`}
                sortId={cell.id}
                onToggle={sortTable as (id: string) => void}
                state={sortState[cell.id]}
              >
                {cell.name}
              </Table.HeadCell>
            );
          })}
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {loadingCurrentEmployments === ThunkRequestStatus.Pending ? (
          <TableLoadingRow />
        ) : (
          employmentListIndices.map(employmentListIndex => (
            <EmploymentsTableRow
              key={`employments-table-row-${employmentListIndex}`}
              employment={employmentList[employmentListIndex]}
              countries={countries}
              mobileLayout={isMobile}
              showIconColumn={showIconColumn}
            />
          ))
        )}
      </Table.Body>
    </Table>
  );
};
